import React, { createContext, useContext, ReactNode } from 'react';

// Toast types
export type ToastType = 'success' | 'error' | 'warning' | 'info';

// Toast message interface
export interface ToastMessage {
  id: string;
  title: string;
  message: string;
  type: ToastType;
  duration?: number;
}

// Toast context
interface ToastContextType {
  showToast: (title: string, message: string, type?: ToastType, duration?: number) => void;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// ============================================================================
// DOM-based Toast Renderer (bypasses React rendering pipeline entirely)
// ============================================================================

const TOAST_CONTAINER_ID = 'dom-toast-container';
const TOAST_STYLE_ID = 'toast-keyframe-styles';

/**
 * Inject CSS styles for toast animations (once)
 */
const ensureToastStyles = () => {
  if (document.getElementById(TOAST_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = TOAST_STYLE_ID;
  style.textContent = `
    @keyframes toast-slide-in {
      from { opacity: 0; transform: translateX(100%); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes toast-slide-out {
      from { opacity: 1; transform: translateX(0); }
      to { opacity: 0; transform: translateX(100%); }
    }
    @keyframes toast-progress {
      from { width: 100%; }
      to { width: 0%; }
    }
    #${TOAST_CONTAINER_ID} {
      position: fixed;
      top: 1rem;
      right: 1rem;
      z-index: 99999;
      pointer-events: none;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      max-width: calc(100vw - 2rem);
    }
    .dom-toast {
      pointer-events: auto;
      width: 24rem;
      max-width: calc(100vw - 2rem);
      animation: toast-slide-in 300ms ease-out forwards;
      font-family: 'Kanit', 'Sarabun', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    .dom-toast.exiting {
      animation: toast-slide-out 300ms ease-out forwards;
    }
    .dom-toast-inner {
      border: 1px solid;
      border-radius: 0.5rem;
      box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
      padding: 1rem;
      position: relative;
      overflow: hidden;
    }
    .dom-toast-progress-track {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background: #e5e7eb;
    }
    .dom-toast-progress-bar {
      height: 100%;
      width: 0%;
    }
    .dom-toast-content {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
    }
    .dom-toast-icon {
      flex-shrink: 0;
      width: 1.25rem;
      height: 1.25rem;
    }
    .dom-toast-text {
      flex: 1;
      min-width: 0;
    }
    .dom-toast-title {
      font-size: 0.875rem;
      font-weight: 600;
      margin: 0;
    }
    .dom-toast-message {
      font-size: 0.875rem;
      margin: 0.125rem 0 0 0;
      white-space: pre-line; /* BUG-S2103-002: สรุปผลลบหลายรายการ/ข้อความ BE ที่มี \n ขึ้นบรรทัดใหม่ */
    }
    .dom-toast-close {
      flex-shrink: 0;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      color: #9ca3af;
      transition: color 150ms;
      line-height: 1;
    }
    .dom-toast-close:hover {
      color: #4b5563;
    }
    .dom-toast-close svg {
      width: 1.25rem;
      height: 1.25rem;
    }

    /* Type-specific colors */
    .dom-toast-success .dom-toast-inner { background: #ecfdf5; border-color: #a7f3d0; }
    .dom-toast-success .dom-toast-icon { color: #10b981; }
    .dom-toast-success .dom-toast-title { color: #065f46; }
    .dom-toast-success .dom-toast-message { color: #047857; }
    .dom-toast-success .dom-toast-progress-bar { background: #10b981; }

    .dom-toast-error .dom-toast-inner { background: #fef2f2; border-color: #fecaca; }
    .dom-toast-error .dom-toast-icon { color: #ef4444; }
    .dom-toast-error .dom-toast-title { color: #991b1b; }
    .dom-toast-error .dom-toast-message { color: #b91c1c; }
    .dom-toast-error .dom-toast-progress-bar { background: #ef4444; }

    .dom-toast-warning .dom-toast-inner { background: #fffbeb; border-color: #fde68a; }
    .dom-toast-warning .dom-toast-icon { color: #f59e0b; }
    .dom-toast-warning .dom-toast-title { color: #92400e; }
    .dom-toast-warning .dom-toast-message { color: #b45309; }
    .dom-toast-warning .dom-toast-progress-bar { background: #f59e0b; }

    .dom-toast-info .dom-toast-inner { background: #eff6ff; border-color: #bfdbfe; }
    .dom-toast-info .dom-toast-icon { color: #3b82f6; }
    .dom-toast-info .dom-toast-title { color: #1e40af; }
    .dom-toast-info .dom-toast-message { color: #1d4ed8; }
    .dom-toast-info .dom-toast-progress-bar { background: #3b82f6; }
  `;
  document.head.appendChild(style);
};

/**
 * Get SVG icon markup for a toast type
 */
const getIconSvg = (type: ToastType): string => {
  switch (type) {
    case 'success':
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>';
    case 'error':
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';
    case 'warning':
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
    default: // info
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
  }
};

/**
 * Ensure the toast container DOM element exists
 */
const ensureContainer = (): HTMLElement => {
  ensureToastStyles();
  let container = document.getElementById(TOAST_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = TOAST_CONTAINER_ID;
    // R3U-08 (8 ก.ย. 2026): container **ไม่ใช่** live region — toast แต่ละใบตั้ง role/aria-live
    // ของตัวเองอยู่แล้ว (status/alert) · เดิมตั้งซ้อนที่ container ด้วย → live region 2 ชั้น
    // screen reader ประกาศซ้ำ และ accessibility tree เห็น "toast 2 ใบข้อความเดียวกัน" ทุกหน้า
    document.body.appendChild(container);
  }
  return container;
};

/**
 * Remove a toast element with exit animation
 */
const removeToast = (el: HTMLElement) => {
  el.classList.add('exiting');
  setTimeout(() => {
    el.remove();
  }, 300);
};

/**
 * Create and show a toast via direct DOM manipulation
 */
// ข้อความของ interceptor กลาง (apiWithLogging P-S043-1) เมื่อยิงไม่ถึง server — ต้องตรงกับที่นั่นทุกตัวอักษร
export const NETWORK_ERROR_MESSAGES = [
  'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต',
  'การเชื่อมต่อใช้เวลานานเกินไป กรุณาลองใหม่อีกครั้ง',
];

const domShowToast = (title: string, message: string, type: ToastType = 'info', duration?: number) => {
  // OBS-S1109-002 FIX: error toasts get 10s default (longer reading time for Thai validation messages)
  // others stay at 5s. Explicit duration arg still overrides.
  if (duration === undefined) {
    duration = type === 'error' ? 10000 : 5000;
  }
  const container = ensureContainer();

  // BUG-S2099-001: ความล้มเหลวครั้งเดียวขึ้น toast ซ้ำ 2 ใบ — interceptor กลาง (apiWithLogging P-S043-1)
  // แจ้ง "การเชื่อมต่อใช้เวลานานเกินไป…" แล้ว catch ของหน้าแปลง error เดียวกันเป็นข้อความเดิมอีกรอบ
  // (เกิดได้ทุกหน้าที่ catch แล้ว showAlert) → ถ้าข้อความเดียวกันทุกตัวอักษรยังแสดงอยู่ ไม่สร้างใบใหม่
  // BUG-S2100-001: ไม่เอา title มาเป็นกุญแจ — hook useMutationWithRefresh ตั้งหัวข้อ "ไม่สามารถดำเนินการได้"
  // ส่วน catch ของหน้าใช้ "เกิดข้อผิดพลาด" กับข้อความ server เดียวกัน (หน้าบริษัท: เลขผู้เสียภาษีซ้ำขึ้น 2 ใบ)
  const dedupKey = `${type}|${message}`;
  const existing = Array.from(container.children).find(
    (c) => (c as HTMLElement).dataset?.toastKey === dedupKey && !c.classList.contains('exiting'),
  );
  if (existing) return;
  // KD3 S2137 (BUG-S2137-003): ออฟไลน์กดบันทึกอุปกรณ์ → 2 ใบ ("ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้…" จาก interceptor กลาง
  // + "ไม่สามารถบันทึกข้อมูลได้" จาก fallback ของหน้า) — กุญแจข้อความตรงตัวจับไม่ได้เพราะข้อความต่างกัน
  // ระหว่างใบ "เชื่อมต่อไม่ได้" ยังแสดงอยู่ error อื่นทุกใบคือผลของเหตุเดียวกัน → ไม่ซ้อนใบกลาง ๆ ทับ
  if (type === 'error' && !NETWORK_ERROR_MESSAGES.includes(message)) {
    const networkShowing = Array.from(container.children).some((c) => {
      const key = (c as HTMLElement).dataset?.toastKey || '';
      return !c.classList.contains('exiting') && NETWORK_ERROR_MESSAGES.some((m) => key === `error|${m}`);
    });
    if (networkShowing) return;
  }

  const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const el = document.createElement('div');
  el.id = id;
  el.dataset.toastKey = dedupKey;
  el.className = `dom-toast dom-toast-${type}`;
  // A11y: errors use assertive (interrupts immediately); others use polite (waits for screen reader to finish)
  el.setAttribute('role', type === 'error' ? 'alert' : 'status');
  el.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
  el.setAttribute('aria-atomic', 'true');

  el.innerHTML = `
    <div class="dom-toast-inner">
      ${duration > 0 ? `
        <div class="dom-toast-progress-track">
          <div class="dom-toast-progress-bar" style="animation: toast-progress ${duration}ms linear forwards;"></div>
        </div>
      ` : ''}
      <div class="dom-toast-content">
        <div class="dom-toast-icon">${getIconSvg(type)}</div>
        <div class="dom-toast-text">
          <p class="dom-toast-title">${escapeHtml(title)}</p>
          <p class="dom-toast-message">${escapeHtml(message)}</p>
        </div>
        <button class="dom-toast-close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </div>
  `;

  // Close button handler
  const closeBtn = el.querySelector('.dom-toast-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => removeToast(el));
  }

  container.appendChild(el);

  // Auto-dismiss
  if (duration > 0) {
    setTimeout(() => {
      if (el.parentNode) {
        removeToast(el);
      }
    }, duration);
  }
};

/**
 * Remove a toast by ID
 */
const domHideToast = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    removeToast(el);
  }
};

/**
 * Simple HTML escaping to prevent XSS
 */
const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// ============================================================================
// React Provider (delegates to DOM-based renderer)
// ============================================================================

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const contextValue: ToastContextType = {
    showToast: domShowToast,
    hideToast: domHideToast,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
};

/**
 * BUG-S1146-001 FIX (continuation of BUG-S1144-001 useAuth defensive pattern):
 * Returns safe no-op defaults if ToastContext is unavailable (Vite HMR race
 * or rare lazy-load timing) instead of throwing. Prevents ErrorBoundary trigger.
 * Logs warning loudly so genuine "forgot ToastProvider" bugs remain visible.
 */
const TOAST_FALLBACK_DEFAULTS: ToastContextType = {
  showToast: () => {},
  hideToast: () => {},
};

// Hook for using toast within components
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    if (typeof console !== 'undefined') {
      console.warn(
        '[useToast] ToastContext unavailable — using safe defaults. ' +
        'Likely Vite HMR race. If seen in production, ensure <ToastProvider> wraps the tree.'
      );
    }
    return TOAST_FALLBACK_DEFAULTS;
  }
  return context;
};

// ============================================================================
// Standalone / Global toast functions
// ============================================================================

export const showToast = domShowToast;
export const hideToast = domHideToast;

// Helper functions for common toast types
export const showSuccess = (message: string, title: string = 'สำเร็จ') => {
  showToast(title, message, 'success');
};

export const showError = (message: string, title: string = 'เกิดข้อผิดพลาด') => {
  showToast(title, message, 'error');
};

export const showWarning = (message: string, title: string = 'คำเตือน') => {
  showToast(title, message, 'warning');
};

export const showInfo = (message: string, title: string = 'ข้อมูล') => {
  showToast(title, message, 'info');
};

// Backward compatibility
export const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
  showToast(title, message, type);
};
